import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Data } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { delay, Observable, switchMap } from 'rxjs';
import { distinctUntilChanged, filter, map, tap, withLatestFrom } from 'rxjs/operators';

import {
    ChangeCurrentCategoryAction,
    CloseMenu,
    InitializeLastSectionFromLocalStore,
    LoadLastSection,
    OpenCategoriesInHeader,
    OpenMenu,
    SetActiveCategories,
    SetActiveCategoryIdLvl1,
    SetLastSection,
    SetLastSectionSuccess,
    SetSeoHome,
} from './menu-categories.actions';
import { getActiveCategories, getLastSectionSelector } from './menu-categories.selectors';
import { ActiveCategoriesTree } from './menu-categories.state';

import { ApplicationSections } from '@core/domain/models/application-section.model';
import { CorePlatformService } from '@core/infrastructure/platform/platform.service';
import * as fromCoreEvents from '@core/infrastructure/state/core-events.actions';
import { getContext } from '@core/infrastructure/state/core.selectors';
import { CoreStorageService } from '@core/infrastructure/storage/storage.service';
import { CategoriesPublicCommands, CategoriesSelectors } from '@features/category';
import {
    findCategoryByProperty,
    getIndexBySection,
    getSectionByIndex,
    isCategoryPage,
    isChildOfCategory, matchingUrlPaths,
} from '@features/category/application/services/category.utils';
import { SidebarActionTypes } from '@features/sidebar';
import { routerPublicQueries } from '@frontend/libs/duttirouter';
import { Resolutions } from '@layout/domain/models/layout.models';
import { getResolution } from '@layout/infrastructure/state/layout.selectors';
import { PageTitleService } from '@layout/ui/services/page-title.service';
import { MenuCategory } from '@shared/header/models/category.model';
import { HeaderService } from '@shared/header/services/header.service';
import { GoToCategory } from '@shared/navigation';

@Injectable()
export class MenuCategoriesEffects {
    private readonly getMenuCategories$:
    Observable<MenuCategory[]> = this.store.select(CategoriesSelectors.getMenuCategories);

    private readonly getLastSectionSelector$: Observable<string> = this.store.select(getLastSectionSelector);

    private readonly getActiveCategories$: Observable<
        Record<
            number, ActiveCategoriesTree
        >> = this.store.select(getActiveCategories);

    private readonly getDataFromRouter$: Observable<Data> = this.store.select(routerPublicQueries.getDataFromRouter);

    private readonly getMergedRoute$ = this.store.select(routerPublicQueries.getMergedRoute);

    private readonly isMobile$ = this.store.select(getResolution)
        .pipe(map(resolution => [Resolutions.MOBILE, Resolutions.TABLET].includes(resolution)));

    // se debe cerrar el menu posteriormente al finalizar la animacion de scroll
    private readonly delayAnimationCloseMenu = 700;

    private readonly getCategoryId$ = this.store.select(getContext).pipe(
        map(context => context?.categoryId),
    );

    constructor(
        private readonly actions$: Actions,
        private readonly pageTitleService: PageTitleService,
        private readonly storageService: CoreStorageService,
        private readonly store: Store,
        private readonly headerService: HeaderService,
        private readonly platform: CorePlatformService,
        @Inject(DOCUMENT) private readonly document: Document,
    ) { }

    public LoadMenuCategoriesSuccessEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CategoriesPublicCommands.LoadMenuCategoriesSuccess),
            map(() => LoadLastSection()),
        );
    });

    public setLastSectionEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetLastSection),
            distinctUntilChanged((a, b) => a.section === b.section),
            map(({ section, navigation }) => SetLastSectionSuccess({ section, navigation })),
        );
    });

    public setCategoryActiveOnCategorySuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ChangeCurrentCategoryAction),
            map(({ category, categories }) => {
                let index = categories.findIndex(cat => isChildOfCategory(cat, category.id));
                if (index < 0) { index = 0; }
                return SetLastSection({ section: getSectionByIndex(index), navigation: false });
            }),
        );
    });

    public setParrillasActiveCategory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ChangeCurrentCategoryAction),
            map(({ categories, category }) => {
                const categoryLevels = this.headerService.getCategoriesLevelsById(category?.id, categories);
                return SetActiveCategories({ data: categoryLevels });
            }),
        );
    });

    public setLastSectionToStorage$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetLastSection),
            filter(() => this.platform.isBrowser()),
            tap((action) => {
                this.storageService.setValue('LAST_SECTION', action.section, 'PERMANENT', 'FUNCTIONAL_PERSONALIZATION');
            }),
        );
    }, { dispatch: false });

    public setSeoHomePageEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(fromCoreEvents.ConfigureHomeLinkSuccess, SetSeoHome),
            concatLatestFrom(() => this.getMergedRoute$),
            tap(([_, data]) => {
                if (data?.url?.includes('/cn/en')) { // China/English case
                    this.pageTitleService.setTitle('麦西姆杜特 (Massimo Dutti)');
                }
            }),
        );
    }, { dispatch: false });

    public showCategoryInMenuSelectorEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                OpenCategoriesInHeader,
            ),
            map((action: any) => action.categoryId),
            concatLatestFrom(() => this.getMenuCategories$),
            tap(([categoryId, categories]) => {
                const index = categories.findIndex(cat => isChildOfCategory(cat, categoryId));
                this.headerService.openMenuByCategoryId(categoryId, index);
            }),
        );
    }, { dispatch: false });

    public setActiveCategoryIdLvl1$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SetActiveCategoryIdLvl1),
            withLatestFrom(this.getActiveCategories$),
            filter(([_, activeCategories]) => !!activeCategories),
            map(([action, activeCategories]) => {
                const newActiveCategories = Object.values(activeCategories).map((catActive, index) => {
                    if (index === action.index) {
                        return {
                            ...catActive,
                            activeCategoryIdLvl1: action.activeCategoryIdLvl1,
                        };
                    }
                    return catActive;
                });
                return SetActiveCategories({ data: newActiveCategories });
            }),
        ));

    public initializeLastSectionFromLocalStore$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(InitializeLastSectionFromLocalStore),
            map(() => {
                return this.storageService.getValue<string>('LAST_SECTION');
            }),
            map(section =>
                SetLastSection({ section: section ?? ApplicationSections.WOMEN, navigation: false })),
        );
    });

    public resetMenuOnOpenMenu$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenMenu, LoadLastSection),
            switchMap(() => {
                return this.getMenuCategories$.pipe(
                    filter(menuCategories => menuCategories?.length > 0),
                );
            }),
            concatLatestFrom(() => [
                this.getDataFromRouter$.pipe(filter(data => !!data)),
                this.getMenuCategories$,
                this.getMergedRoute$.pipe(filter(data => !!data)),
                this.getLastSectionSelector$,
                this.getCategoryId$,
            ]),
            filter(([_, { pageName }]) => !isCategoryPage(pageName)),
            map(([__, _, menuCategories, merged, lastSection = 'WOMEN', currentCategoryId]) => {
                let categoryId = currentCategoryId;
                const path = merged?.params?.path?.length > 0 ? merged.params.path : merged.url;
                if (!categoryId) {
                    const sectionIndex = getIndexBySection(lastSection);
                    categoryId = findCategoryByProperty(
                        menuCategories[sectionIndex].subcategories,
                        path,
                        'locationPath',
                        matchingUrlPaths,
                    )?.id;
                }

                return {
                    menuCategories,
                    categoryId,
                };
            }),
            map(({ menuCategories, categoryId }) => {
                const newActiveCategories = this.headerService.getCategoriesLevelsById(categoryId, menuCategories);
                return SetActiveCategories({ data: newActiveCategories });
            }),
        );
    });

    public closeSidebarWhenOpenMenu$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenMenu),
            map(() => SidebarActionTypes.CloseSidebar({ toShopCart: false })),
        );
    });

    public disableOverflowWhenMenuIsOpen$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenMenu),
            filter(() => this.platform.isBrowser()),
            tap(() => {
                this.document.body.classList.add('menu-is-open');
            }),
        );
    }, { dispatch: false });

    public enableOverflowWhenMenuIsClose$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CloseMenu),
            filter(() => this.platform.isBrowser()),
            tap(() => {
                this.document.body.classList.remove('menu-is-open');
            }),
        );
    }, { dispatch: false });

    public closeMenuOnNavigate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GoToCategory),
            concatLatestFrom(() => [this.isMobile$]),
            filter(([_, isMobile]) => isMobile),
            delay(this.delayAnimationCloseMenu),
            map(() => CloseMenu()),
        );
    });
}
